import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'

import { cn } from '@/utils'

interface SubnavItemProps extends React.ComponentPropsWithoutRef<'button'> {
  className?: string
  children?: React.ReactNode
  active?: boolean
  asChild?: boolean
}

const SubnavItem = React.forwardRef<HTMLButtonElement, SubnavItemProps>(
  ({ className, children, active, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    const itemRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {
      if (active && itemRef.current) {
        itemRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      }
    }, [active])

    return (
      <div
        ref={itemRef}
        className="relative inline-flex items-center justify-center shrink-0"
      >
        <span
          className={cn(
            'absolute inset-0 z-0 rounded-md bg-white border border-border transition-all duration-200 ease-out',
            active ? 'opacity-100 scale-100' : 'opacity-0 scale-95',
          )}
          aria-hidden="true"
        />

        <Comp
          ref={ref}
          data-state={active ? 'active' : 'inactive'}
          className={cn(
            'relative z-10 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer',
            'hover:text-blue-6',
            'data-[state=active]:text-emerald-500',
            className,
          )}
          {...props}
        >
          {children}
        </Comp>
      </div>
    )
  },
)
SubnavItem.displayName = 'SubnavItem'

export { SubnavItem }
